import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useFilters } from "../../../context/FilterContext";
import {
    Box, TextField, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, TablePagination,
    Typography, CircularProgress, Button, Autocomplete, Tooltip, IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    getAttendanceByCoe,
    getReportForAdmin,
    getTimeSlotsByLabType
} from "../../../services/reportServices/adminReportServices/AttendanceReportServices.js";
import { getAllCOE, getAllSemesters } from "../../../services/adminServices/CoeServices.js";
import { getLabType } from "../../../services/mentorServices/MentorServices.js";
import AdminAttendanceModal from "./AdminAttendanceModal.jsx";
import AdminAttendanceDetailsModal from "./AdminAttendanceDetailsModal.jsx";
import AdminTotalRegisteredModal from "./AdminTotalRegisteredModal.jsx";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const AdminAttendanceReport = () => {
    const { adminAttendanceState, setAdminAttendanceState } = useFilters();

    const [data, setData] = useState(adminAttendanceState.data);
    const [attendanceOptions, setAttendanceOptions] = useState(adminAttendanceState.options.attendanceOptions || []);
    const [timeSlots, setTimeSlots] = useState(adminAttendanceState.options.timeSlots || []);
    const [allCOE, setCOE] = useState(adminAttendanceState.options.allCOE || []);
    const [labTypes, setLabTypes] = useState(adminAttendanceState.options.labTypes || []);
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);
    const isCoeReportViewer = user?.role === "coe_report_viewer";
    const userCoeId = user?.coeId || user?.coe_id || user?.coeCenter?.id || user?.coe_center_id || user?.coe || "";

    const [selection, setSelection] = useState(() => {
        if (isCoeReportViewer) {
            const base = adminAttendanceState.selection || {};
            return {
                coe: userCoeId,
                labType: base.labType || "",
                attendanceName: base.attendanceName || "",
                timeSlot: base.timeSlot || "",
                fromDate: base.fromDate || "",
                toDate: base.toDate || "",
                semester: base.semester || ""
            };
        }
        if (adminAttendanceState.selection && adminAttendanceState.selection.coe) {
            return adminAttendanceState.selection;
        }
        return {
            coe: userCoeId,
            labType: "",
            attendanceName: "",
            timeSlot: "",
            fromDate: "",
            toDate: "",
            semester: ""
        };
    });

    const [selectedAttendance, setSelectedAttendance] = useState();
    const [page, setPage] = useState(adminAttendanceState.page);
    const [rowsPerPage, setRowsPerPage] = useState(adminAttendanceState.rowsPerPage);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState({});
    const [showTable, setShowTable] = useState(adminAttendanceState.showTable);
    const [open, setOpen] = useState(false);
    const [learnerModal, setLearnerModal] = useState(false);
    const [totalRegisteredModal, setTotalRegisteredModal] = useState(false);
    const [searchText, setSearchText] = useState(adminAttendanceState.searchText);
    const [semesters, setSemesters] = useState(adminAttendanceState.options.semesters || []);
    const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const lastFetchedParamsRef = useRef(null);
    const isRefreshingRef = useRef(false);


    useEffect(() => {
        setAdminAttendanceState({
            selection, searchText, data, showTable, page, rowsPerPage, 
            options: { allCOE, labTypes, attendanceOptions, timeSlots, semesters },
            hasFetched: adminAttendanceState.hasFetched
        });
    }, [selection, searchText, data, showTable, page, rowsPerPage, 
        allCOE, labTypes, attendanceOptions, timeSlots, semesters,
        setAdminAttendanceState, adminAttendanceState.hasFetched]);


    useEffect(() => {
        if (!selection.coe) {
            setSemesters([]);
            return;
        }

        const fetchSemesters = async () => {
            try {
                const res = await getAllSemesters(selection.coe);
                const sortedSemesters =
                    res?.data?.data?.sort((a, b) => a - b) || [];

                setSemesters(sortedSemesters);
            } catch (error) {
                setSemesters([]);
                toast.error(
                    error?.response?.data?.message ||
                    "Error fetching semesters"
                );
            }
        };

        if (semesters.length === 0) fetchSemesters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.coe]);

    useEffect(() => {
        if (isCoeReportViewer && userCoeId && selection.coe !== userCoeId) {
            setSelection(prev => ({ ...prev, coe: userCoeId }));
        }
    }, [isCoeReportViewer, userCoeId, selection.coe]);

    useEffect(() => {
        if (!isCoeReportViewer && allCOE.length === 0) {
            getAllCOE()
                .then(res => {
                    const sortedLabs =
                        res.data.data?.sort((a, b) =>
                            a.name?.localeCompare(b.name)
                        ) || [];
                    setCOE(sortedLabs);
                })
                .catch(() => toast.error("Failed to fetch COEs"));
        }

        if (labTypes.length === 0) {
            getLabType()
                .then(res => setLabTypes(res?.data?.data?.value || []))
                .catch(() => setLabTypes([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selection.coe && selection.labType) {
            if (attendanceOptions.length === 0) {
                getAttendanceByCoe(selection.coe, selection.labType)
                    .then(res => setAttendanceOptions(res?.data?.data || []))
                    .catch(() => setAttendanceOptions([]));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.coe, selection.labType]);

    useEffect(() => {
        if (selection.coe && selection.labType && selection.attendanceName) {
            if (timeSlots.length === 0) {
                getTimeSlotsByLabType(
                    selection.coe,
                    selection.labType,
                    selection.attendanceName
                )
                    .then(res => setTimeSlots(res?.data?.data || []))
                    .catch(() => setTimeSlots([]));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.coe, selection.labType, selection.attendanceName]);

    const isValidSelection = useMemo(() => {
        return selection.coe && selection.semester && selection.labType;
    }, [selection]);


    const handleSelectionChange = ({ target: { name, value } }) => {
        const updated = { ...selection, [name]: value };

        if (name === "coe")
            updated.labType = updated.attendanceName = updated.timeSlot = "";
        else if (name === "labType")
            updated.attendanceName = updated.timeSlot = "";
        else if (name === "attendanceName")
            updated.timeSlot = "";

        if (["fromDate", "toDate"].includes(name)) {
            const { fromDate, toDate } = { ...updated, [name]: value };
            if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
                toast.error("'To Date' should be after 'From Date'");
                return;
            }
        }

        setSelection(updated);
    };

    const fetchAllData = async () => {
        if (!isValidSelection) return;
        setLoading(true);
        try {
            setAdminAttendanceState(prev => ({ ...prev, hasFetched: true }));
            const res = await getReportForAdmin(
                selection.coe,
                selection.labType,
                selection.timeSlot,
                selection.fromDate,
                selection.toDate,
                selection.attendanceName,
                selection.semester
            );

            setData(res?.data?.data || []);
            setShowTable(true);
            setSearchText("");
            setPage(0);
            setLastRefreshed(new Date());
            lastFetchedParamsRef.current = {
                coe: selection.coe,
                labType: selection.labType,
                timeSlot: selection.timeSlot,
                fromDate: selection.fromDate,
                toDate: selection.toDate,
                attendanceName: selection.attendanceName,
                semester: selection.semester,
            };
        } catch (err) {
            toast.error(err?.response?.data?.message || "Error fetching data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const refreshReportData = useCallback(async (isSilent = true) => {
        const params = lastFetchedParamsRef.current;
        if (!params || !params.coe || !params.labType) return;
        if (isRefreshingRef.current) return;

        isRefreshingRef.current = true;
        if (!isSilent) {
            setLoading(true);
        } else {
            setIsAutoRefreshing(true);
        }

        try {
            const res = await getReportForAdmin(
                params.coe,
                params.labType,
                params.timeSlot,
                params.fromDate,
                params.toDate,
                params.attendanceName,
                params.semester
            );
            setData(res?.data?.data || []);
            setLastRefreshed(new Date());
            if (!isSilent) {
                toast.success("Attendance report refreshed successfully.");
            }
        } catch (error) {
            console.error("Error refreshing attendance report:", error);
            if (!isSilent) {
                toast.error(error?.response?.data?.message || "Error refreshing attendance report");
            }
        } finally {
            isRefreshingRef.current = false;
            if (!isSilent) {
                setLoading(false);
            } else {
                setIsAutoRefreshing(false);
            }
        }
    }, []);

    // Auto-refresh every 1 minute while table is visible
    useEffect(() => {
        if (!showTable) return;

        const intervalId = setInterval(() => {
            refreshReportData(true);
        }, 60000);

        return () => clearInterval(intervalId);
    }, [showTable, lastRefreshed, refreshReportData]);

    const highlightText = (text, search) => {
        if (!search) return text;
        const regex = new RegExp(`(${search})`, "gi");
        const parts = String(text || "").split(regex);

        return parts.map((part, i) =>
            part.toLowerCase() === search.toLowerCase() ? (
                <span key={i} style={{ backgroundColor: "yellow", fontWeight: 600 }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const filteredData = useMemo(() => {
        if (!searchText) return data;
        const search = searchText.toLowerCase();
        return data.filter(row =>
            Object.values(row).some(value =>
                String(value || "").toLowerCase().includes(search)
            )
        );
    }, [data, searchText]);

    const sortedData = useMemo(() =>
        [...filteredData].sort(
            (a, b) => new Date(b.AttendanceDate) - new Date(a.AttendanceDate)
        ),
        [filteredData]
    );

    const paginatedRows = useMemo(() =>
        sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page, rowsPerPage]
    );

    const handleClick = (row) => {
        setSelectedAttendance(row);
        setOpen(true);
        const coeObj = allCOE.find((c) => String(c.id || c.coe_id) === String(selection?.coe));
        setSelected({
            ...row,
            ...selection,
            coe_name: coeObj?.name || coeObj?.coe_name || row?.coe_name,
            lab_name: row?.lab_name || row?.labName,
        });
    };

    const handleOpenCreateModuleModal = (row, action) => {
        setLearnerModal(true);
        const coeObj = allCOE.find((c) => String(c.id || c.coe_id) === String(selection?.coe));
        setSelected({
            ...row,
            ...selection,
            coe: selection?.coe,
            coe_name: coeObj?.name || coeObj?.coe_name || row?.coe_name,
            lab: row?.labId,
            lab_name: row?.lab_name || row?.labName,
            date: row?.AttendanceDate,
            attendanceId: row?.dailyAttendanceId,
            action,
            attendanceName: row?.attendanceName
        });
    };

    const handleOpenTotalRegisteredModal = (row) => {
        setTotalRegisteredModal(true);
        setSelected({
            coe: selection?.coe,
            labId: row?.labId,
        });
    };

    return (
        <Box>
            <Typography className="dashboard-header-title" pt={isCoeReportViewer ? 0 : 2} pb={4} width="100%">
                Welcome {JSON.parse(localStorage.getItem("user"))?.name || "User"}! to the Attendance Report.
            </Typography>

            <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, 
                gap: 2, 
                minWidth: "100%" 
            }}>
                {[{ label: "COE*", name: "coe", options: allCOE },
                { label: "Lab Type*", name: "labType", options: labTypes }]
                .filter(item => !isCoeReportViewer || item.name !== "coe")
                .map(({ label, name, options }) => (
                    <TextField
                        key={name}
                        select
                        label={label}
                        name={name}
                        value={selection[name]}
                        onChange={handleSelectionChange}
                        size="small"
                        variant="outlined"
                        sx={{ width: "100%" }}
                    >
                        <MenuItem value="">None</MenuItem>
                        {options.map((opt, i) => (
                            <MenuItem key={i} value={opt.id || opt}>
                                {opt.name || opt}
                            </MenuItem>
                        ))}
                    </TextField>
                ))}

                <TextField
                    select
                    label="Semester*"
                    name="semester"
                    value={selection.semester}
                    onChange={handleSelectionChange}
                    size="small"
                    variant="outlined"
                    sx={{ width: "100%" }}
                    disabled={!selection.coe}
                >
                    <MenuItem value="">None</MenuItem>
                    {semesters.filter(sem => sem != null).map((sem) => (
                        <MenuItem key={sem} value={sem}>
                            {sem}
                        </MenuItem>
                    ))}
                </TextField>

                <Autocomplete
                    options={attendanceOptions?.sort((a, b) =>
                        a.attendanceName?.localeCompare(b.attendanceName)
                    )}
                    getOptionLabel={(option) => option.attendanceName || ""}
                    value={attendanceOptions.find(opt => opt.attendanceName === selection.attendanceName) || null}
                    onChange={(e, newValue) => {
                        handleSelectionChange({
                            target: { name: "attendanceName", value: newValue?.attendanceName || "" }
                        });
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Attendance Name"
                            variant="outlined"
                            size="small"
                            sx={{ width: "100%" }}
                        />
                    )}
                />

                <TextField
                    select
                    label="Session Time"
                    name="timeSlot"
                    value={selection.timeSlot}
                    onChange={handleSelectionChange}
                    size="small"
                    variant="outlined"
                    sx={{ width: "100%" }}
                >
                    <MenuItem value="">None</MenuItem>
                    {timeSlots.map((opt, i) => (
                        <MenuItem key={i} value={opt.timeSlot}>
                            {opt.timeSlot}
                        </MenuItem>
                    ))}
                </TextField>

                {["From Date", "To Date"].map((label, i) => (
                    <TextField
                        key={label}
                        type="date"
                        name={i === 0 ? "fromDate" : "toDate"}
                        value={selection[i === 0 ? "fromDate" : "toDate"]}
                        label={label}
                        onChange={handleSelectionChange}
                        size="small"
                        sx={{ width: "100%" }}
                        InputLabelProps={{ shrink: true }}
                    />
                ))}

                <Button
                    variant="contained"
                    sx={{ width: "100%", height: "40px" }}
                    onClick={fetchAllData}
                    disabled={!isValidSelection || loading}
                >
                    {loading ? <CircularProgress size={24} /> : "VIEW"}
                </Button>
            </Box>

            {showTable && (
                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <TextField
                            label="Search"
                            placeholder="Search by lab name..."
                            size="small"
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                setPage(0);
                            }}
                            sx={{ minWidth: "300px" }}
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                backgroundColor: "#4caf50",
                                boxShadow: "0 0 6px #4caf50",
                                display: "inline-block",
                            }}
                        />
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 13, fontWeight: 500 }}>
                            Auto-refreshes every 1 min
                            {lastRefreshed && (
                                <span style={{ marginLeft: 6, color: "#888" }}>
                                    (Last updated: {lastRefreshed.toLocaleTimeString()})
                                </span>
                            )}
                        </Typography>
                        {isAutoRefreshing && (
                            <CircularProgress size={14} thickness={5} sx={{ color: "#4caf50", ml: 0.5 }} />
                        )}
                        <Tooltip title="Refresh Report" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => refreshReportData(false)}
                                    disabled={loading || isAutoRefreshing}
                                    sx={{ color: "primary.main" }}
                                >
                                    <RefreshIcon
                                        fontSize="small"
                                        sx={{
                                            animation: (isAutoRefreshing || loading) ? "spin 1s linear infinite" : "none",
                                            "@keyframes spin": {
                                                "0%": { transform: "rotate(0deg)" },
                                                "100%": { transform: "rotate(360deg)" },
                                            },
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {showTable && (
                <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto", mt: 1 }}>
                    <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                        <TableHead>
                            <TableRow>
                                {["Date", "LabName", "Attendance Name", "Session Time", "Semester",
                                    "Total Registered", "Max Attended",
                                    "#Attended", "#Not Attended", "% Attended"
                                ].map((header) => (
                                    <StyledTableCell key={header}>{header}</StyledTableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.length ? paginatedRows.map((row, idx) => (
                                <StyledTableRow key={idx}>
                                    <StyledTableCell>{highlightText(row.AttendanceDate || "-", searchText)}</StyledTableCell>
                                    <StyledTableCell>{highlightText(row.lab_name || "-", searchText)}</StyledTableCell>
                                    <StyledTableCell sx={{ textTransform: "none !important" }}>{highlightText(row.attendanceName || "-", searchText)}</StyledTableCell>
                                    <StyledTableCell>{highlightText(row.SessionTime || "-", searchText)}</StyledTableCell>
                                    <StyledTableCell>{highlightText(row.semester || "-", searchText)}</StyledTableCell>
                                    {/* <StyledTableCell>{highlightText(row.TotalLearners || "-", searchText)}</StyledTableCell> */}

                                    <StyledTableCell
                                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => handleOpenTotalRegisteredModal(row)}
                                    >
                                        {highlightText(row.TotalLearners || "-", searchText)}
                                    </StyledTableCell>


                                    <StyledTableCell
                                        onClick={() => handleClick(row)}
                                        style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
                                    >
                                        {highlightText(row?.MaxAttended || 0, searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell
                                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => handleOpenCreateModuleModal(row, "Present")}
                                    >
                                        {highlightText(row?.Attended || 0, searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell
                                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => handleOpenCreateModuleModal(row, "Absent")}
                                    >
                                        {highlightText(row?.NotAttended || 0, searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell>
                                        {highlightText(Math.round(row.PercentageAttended) || 0, searchText)}%
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <TableRow>
                                    <StyledTableCell colSpan={9} align="center">
                                        No records found.
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[10, 20, 30, 60, 100]}
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>
            )}

            <AdminTotalRegisteredModal
                selected={selected}
                open={totalRegisteredModal}
                onClose={() => setTotalRegisteredModal(false)}
            />

            <AdminAttendanceModal
                open={open}
                onClose={() => setOpen(false)}
                selected={selected}
                selectedAttendance={selectedAttendance}
            />

            <AdminAttendanceDetailsModal
                selected={selected}
                open={learnerModal}
                onClose={() => setLearnerModal(false)}
            />
        </Box>
    );
};

export default AdminAttendanceReport;
